import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { get } from 'lodash';
import { SearchFiltersService } from '../../services/search-filters.service';
import { takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import {
  DealerFilter,
  DeselectAllVehiclesOnPage,
  LaunchDarklyService,
  SearchFilterQueries,
  SelectAllVehiclesOnPage,
  SellConfigurationState,
  SellToggleState,
  StatusFilter,
  ToggleStateService
} from 'remarketing-angular-library';
import { AnalyticsService } from 'smartauction-frontend-micro-library';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  UtilityBarDateRangeComponent
} from './utility-bar-date-range/utility-bar-date-range.component';
import {
  SearchResultsSortSelectComponent
} from '../search-results-sort-select/search-results-sort-select.component';
import { TrapFocusComponent } from 'remarketing-ui-components-lib';
import {
  MobileSearchFilterComponent
} from '../search-filter/mobile-search-filter/mobile-search-filter.component';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'lib-utility-bar',
    templateUrl: './utility-bar.component.html',
    styleUrls: ['./utility-bar.component.scss'],
    imports: [
        TranslocoDirective,
        UtilityBarDateRangeComponent,
        SearchResultsSortSelectComponent,
        TrapFocusComponent,
        MobileSearchFilterComponent,
        CommonModule
    ]
})
export class UtilityBarComponent implements OnInit, OnDestroy {
  tenantName: string;

  @Select(SearchFilterQueries.statusFilters) readonly statusFilter$: Observable<StatusFilter>;
  @Select(SellToggleState.selectAllChecked) readonly selectAllChecked$: Observable<boolean>;
  @Select(SellConfigurationState.isListViewToggled) readonly isListView$: Observable<boolean>;

  @Input() isExporting = false;
  @Input() showCreateDraftBulkSale = false;
  @Input() isSellReportingFeatureEnabled;
  @Input() inServiceVehiclesEnabled;
  @Input() singleListSelected;
  @Input() disableSelectAll;
  @Input() isFilterByInServiceVehicles: boolean = false;
  @Output() exported = new EventEmitter<void>();
  @Output() mobileFilter = new EventEmitter<boolean>();
  @ViewChild('exportButton') exportButton;

  private window: Window;
  private _mobileFiltersVisible = false;
  private destroyed$ = new Subject();
  selectedStatusFilter: StatusFilter;
  soldStatusFilterSelected = false;
  offersByDealerFilterSelectedAndActive: boolean;
  dateRangeError = false;
  selectAllChecked: boolean;
  selectAllAdminChecked: boolean;
  isListView: boolean;

  get mobileFiltersVisible() {
    return this._mobileFiltersVisible;
  }

  set mobileFiltersVisible(val: boolean) {
    if (val) {
      this.renderer.addClass(this.window.document.body, 'search-results--mobile-filters-open');
    } else {
      this.renderer.removeClass(this.window.document.body, 'search-results--mobile-filters-open');
    }
    this._mobileFiltersVisible = val;
  }

  get noMargin(): boolean {
    return !this.soldStatusFilterSelected && this.showCreateDraftBulkSale;
  }

  get showSellActiveDateRange() {
    return this.offersByDealerFilterSelectedAndActive;
  }

  get showDateRange() {
    return this.showSellActiveDateRange;
  }

  get isSelectAllEnabled() {
    return (
      ((this.isSellReportingFeatureEnabled && this.soldStatusFilterSelected) ||
        (this.isOfferListSellerSelectAllEnabled && this.singleListSelected) ||
        (this.inServiceVehiclesEnabled &&
          this.isFilterByInServiceVehicles &&
          this.selectedStatusFilter === StatusFilter.ACTIVE_VEHICLES) ||
        this.selectedStatusFilter === StatusFilter.RESERVED_VEHICLES) &&
      !this.disableSelectAll
    );
  }

  get isAdminSelectAllEnabled() {
    return (
      this.launchDarklyService.getFeatureFlag('sell-adminSelectAllEnabled', false) &&
      this.selectedStatusFilter !== StatusFilter.REMOVED_VEHICLES
    );
  }

  get manageInventoryMaxSelections(): number {
    return this.launchDarklyService.getFeatureFlag('sell-manageInventoryMaxSelections', -1);
  }

  get disableCheckbox(): boolean {
    return (
      !this.selectAllChecked &&
      this.manageInventoryMaxSelections > -1 &&
      this.store.selectSnapshot(SellToggleState.selectedVehicles)?.length >= this.manageInventoryMaxSelections
    );
  }

  toggleSelectAll() {
    this.analyticsService.trackEvent('checkbox', 'Selected all vehicles - ' + !this.selectAllChecked);
    if (this.selectAllChecked) {
      this.store.dispatch(new DeselectAllVehiclesOnPage());
    } else {
      this.store.dispatch(new SelectAllVehiclesOnPage());
    }
  }

  get isExportResultsEnabled() {
    return this.launchDarklyService.getFeatureFlag('sell-exportResultsEnabled', false);
  }

  constructor(
    private store: Store,
    private renderer: Renderer2,
    @Inject('window') window: any,
    readonly toggleState: ToggleStateService,
    readonly sfService: SearchFiltersService,
    readonly launchDarklyService: LaunchDarklyService,
    readonly analyticsService: AnalyticsService
  ) {
    this.window = window as Window;
  }

  ngOnInit() {
    this.store
      .select(_state => _state.configuration.configuration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(_configuration => {
        this.tenantName = get(_configuration, 'tenantName', null);
      });

    this.selectAllChecked$.pipe(takeUntil(this.destroyed$)).subscribe(selectAll => (this.selectAllChecked = selectAll));

    this.isListView$.pipe(takeUntil(this.destroyed$)).subscribe(isListView => (this.isListView = isListView));

    this.sfService.offerFiltersState$.pipe(takeUntil(this.destroyed$)).subscribe(offerFilterState => {
      this.offersByDealerFilterSelectedAndActive =
        (offerFilterState.openFilter === 'offers' || offerFilterState.openFilter === undefined) &&
        offerFilterState.dealerFilter === DealerFilter.OFFERS_BY_DEALER;
    });

    this.statusFilter$.pipe(takeUntil(this.destroyed$)).subscribe(statusFilter => {
      this.selectedStatusFilter = statusFilter;
      this.soldStatusFilterSelected = statusFilter === StatusFilter.SOLD_VEHICLES;
    });
  }

  export() {
    this.exported.emit();
  }

  toggleMobileFilter() {
    this.mobileFiltersVisible = !this.mobileFiltersVisible;
    this.mobileFilter.emit(this.mobileFiltersVisible);
    if (this.mobileFiltersVisible) {
      document.querySelector('#manage-header').setAttribute('aria-hidden', 'true');
      document.querySelector('#search-form').setAttribute('aria-hidden', 'true');
      document.querySelector('#active-vehicles-count').setAttribute('aria-hidden', 'true');
      document.querySelector('#utility-bar').setAttribute('aria-hidden', 'true');
      document.querySelector('#layout-body').setAttribute('aria-hidden', 'true');
    } else {
      document.querySelector('#manage-header').setAttribute('aria-hidden', 'false');
      document.querySelector('#search-form').setAttribute('aria-hidden', 'false');
      document.querySelector('#active-vehicles-count').setAttribute('aria-hidden', 'false');
      document.querySelector('#utility-bar').setAttribute('aria-hidden', 'false');
      document.querySelector('#layout-body').setAttribute('aria-hidden', 'false');
    }
  }

  handleDateRangeError(hasError: boolean) {
    this.dateRangeError = hasError;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get isOfferListSellerSelectAllEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('offerlist-seller-selectAllEnabled', true);
  }
}
